import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPlantProtection,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticePlantProtections,
  useKeyedFormList,
  useMutateFootprintingCropPlantProtection,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_PLANT_PROTECTION_UNITS, PRACTICE_SLUG, UploadablePlantProtection } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tooltip,
  Typography,
} from "components";

type CropPlantProtectionProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const plantProtectionPlaceholder: UploadablePlantProtection = {
  id: undefined,
  practicesPlantProtectionId: "",
  amount: null,
  amountUnit: null,
};

const CropPlantProtectionForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropPlantProtectionProps) => {
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation(["footprintingCrop", "practices"]);

  const [plantProtectionActive, setPlantProtectionActive] = useState<boolean | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const plantProtectionQuery = useGetFootprintingCropPlantProtection(primaryDataId);
  const practicePlantProtectionQuery = useGetPracticePlantProtections();
  const isLoading = [cropPrimaryDataQuery, plantProtectionQuery, practicePlantProtectionQuery].some(queryIsLoading);
  const keyedPlantProtections = useKeyedFormList<UploadablePlantProtection>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-farm-plant-protection-select_plant-protection";

  const { mutate: mutateFarmCropPlantProtection } = useMutateFootprintingCropPlantProtection(
    farmId,
    cropId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedPlantProtection: keyedPlantProtections.payload.filter((p) => !!p.practicesPlantProtectionId),
    },
    primaryDataId
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      plantProtectionActive,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
        mutateFarmCropPlantProtection();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateFarmCropPlantProtection, readOnly]
  );

  useEffect(() => {
    if (plantProtectionQuery.data) {
      keyedPlantProtections.actions.hydrate(
        plantProtectionQuery.data.length
          ? plantProtectionQuery.data.map((p) => ({
              id: p.id,
              practicesPlantProtectionId: p.practicesPlantProtectionId ?? "",
              amount: p.amount ?? null,
              amountUnit: p.amountUnit ?? null,
            }))
          : [plantProtectionPlaceholder]
      );
    }
  }, [plantProtectionQuery.data, keyedPlantProtections.actions]);

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setPlantProtectionActive(cropPrimaryDataQuery.data.plantProtectionActive ?? null);

      formRef.current?.setFieldsValue({
        plantProtectionActive: cropPrimaryDataQuery.data.plantProtectionActive ?? null,
      });

      if (cropPrimaryDataQuery.data.plantProtectionActive !== null) {
        keyedPlantProtections.actions.requestRehydrationValidation();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, keyedPlantProtections.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-crop-plant-protection-form" className="padding-100">
      <Flex align="center" gap={16} justify="space-between" className="padding-100">
        <Typography variant="paragraph-m" style={{ maxWidth: "64%" }}>
          {t("cropPlantProtectionForm.question.plantProtectionQuestionLabel")}
          {true && <span className="required-asterisk">*</span>}
          <Tooltip title={t("cropPlantProtectionForm.tooltips.plantProtectionQuestionLabel")}>
            <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
          </Tooltip>
        </Typography>
        <KlimRadioGroup
          required
          name="plantProtectionActive"
          dataTestId="footprinting-crop-plant-protection-radio"
          disabled={readOnly}
          vertical={false}
          type="tertiary"
          size="large"
          onChange={({ target }) => {
            setPlantProtectionActive(target.value);
          }}
          value={plantProtectionActive}
          defaultValue={plantProtectionActive}
          optionType="button"
          options={[true, false].map((opt) => ({
            label: t(`footprinting:radioAnswer.${opt}`),
            value: opt,
          }))}
        />
      </Flex>
      <Form ref={formRef} preserve={false}>
        {plantProtectionActive &&
          keyedPlantProtections.entries.map((protection, index) => {
            const selectGroupWithIndex = `${selectGroupName}-${index}`;

            return (
              <Form.Item key={protection.clientKey} className="padding-100">
                <Flex vertical gap={16}>
                  <Flex justify="space-between" align="center" vertical={false}>
                    <Typography variant="h5" style={{ flex: 1 }}>
                      {t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}
                    </Typography>
                    <IconButton
                      type="tertiary"
                      dataTestId={`${selectGroupWithIndex}-delete`}
                      disabled={readOnly}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => {
                        keyedPlantProtections.actions.remove(protection.clientKey);
                        if (keyedPlantProtections.entries.length === 1) setPlantProtectionActive(null);
                      }}
                    />
                  </Flex>
                  <Select
                    disabled={readOnly}
                    name={`${selectGroupName}-${protection.clientKey}`}
                    dataTestId={selectGroupWithIndex}
                    label={t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel")}
                    tooltip={t("cropPlantProtectionForm.tooltips.plantProtectionTypeQuestionLabel")}
                    rules={[{ required: true, message: "" }]}
                    initialValue={practicePlantProtectionQuery.data?.find((p) => p.id === protection.practicesPlantProtectionId)?.name || ""}
                    value={practicePlantProtectionQuery.data?.find((p) => p.id === protection.practicesPlantProtectionId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = practicePlantProtectionQuery.data?.find((p) => p.name === v);
                      keyedPlantProtections.actions.update(protection.clientKey, { practicesPlantProtectionId: matchingPractice ? matchingPractice.id : "" });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    showSearch
                  >
                    {(practicePlantProtectionQuery.data ?? []).map((practice) => (
                      <Option
                        disabled={readOnly}
                        key={`${selectGroupWithIndex}-${practice.id}`}
                        data-testid={`${selectGroupWithIndex}-${practice.id}`}
                        value={practice.name}
                      >
                        {t(`${PRACTICE_SLUG.PLANT_PROTECTIONS}.${practice.translationSlug}`, { ns: "practices" }) || practice.name}
                      </Option>
                    ))}
                  </Select>

                  <InputNumberWithUnitSelector
                    className="floating-label"
                    disabled={readOnly}
                    dataTestId={`footprinting-crop-plant-protection-amount-input-${index}`}
                    name={`amountAppliedKgPerHa-${protection.clientKey}`}
                    label={t("cropPlantProtectionForm.question.amountAppliedQuestionLabel")}
                    tooltip={t("cropPlantProtectionForm.tooltips.amountAppliedQuestionLabel")}
                    unitLabel={t("unit", { ns: "footprinting" })}
                    units={["kilograms", "liters"]}
                    value={protection.amount}
                    initialValue={protection.amount}
                    unitValue={protection.amountUnit}
                    onChange={(v, unit) =>
                      keyedPlantProtections.actions.update(protection.clientKey, { amount: v, amountUnit: unit as ENUM_PLANT_PROTECTION_UNITS })
                    }
                    rules={[
                      { required: true, message: t("cropPlantProtectionForm.error.amountAppliedLabelError") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("cropPlantProtectionForm.error.amountAppliedLabelError")));
                        },
                      },
                    ]}
                  />
                </Flex>
              </Form.Item>
            );
          })}
        {plantProtectionActive && (
          <Button
            type="text"
            disabled={readOnly}
            onClick={() => {
              keyedPlantProtections.actions.add(plantProtectionPlaceholder);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("cropPlantProtectionForm.add")}
          </Button>
        )}
      </Form>
    </Card>
  );
};

CropPlantProtectionForm.Key = "crop.plantProtection";
export { CropPlantProtectionForm };
